export const routePaths = {
  dashboard: "/",

  // Marketplace
  strategies: "/strategies",
  strategyDetails: "/strategies/:id",
  // Ranked above the :id route by React Router, which scores a static segment higher than a dynamic
  // one — so "request" is never read as a strategy id.
  requestStrategy: "/strategies/request",
  cart: "/cart",
  checkout: "/checkout",
  myStrategies: "/my-strategies",
  memberships: "/memberships",
  requestMembership: "/memberships/request",
  membershipDetails: "/memberships/:tierId",
  membershipEnd: "/memberships/end/:tierId",
  membershipResume: "/memberships/resume/:tierId",
  addOns: "/add-ons",
  addOnDetails: "/add-ons/:addOnId",
  addOnEnd: "/add-ons/end/:addOnId",
  addOnResume: "/add-ons/resume/:addOnId",
  portfolioBuilder: "/portfolio-builder",
  // The same page as strategyDetails, reached from My strategies, so its breadcrumb and back button lead there.
  myStrategyDetails: "/my-strategies/:id",
  subscriptionEnd: "/subscriptions/end/:strategyId",
  subscriptionResume: "/subscriptions/resume/:strategyId",
  subscriptionStayMonthly: "/subscriptions/stay-monthly/:strategyId",
  subscriptionDetail: "/subscriptions/:strategyId",

  // Signals
  signals: "/signals",
  signalsConfigure: "/signals/configure",
  signalsConfigureChange: "/signals/configure/change",
  // Ranked above signalDetail for the same reason requestStrategy is above strategyDetails.
  portfolioTracker: "/signals/portfolio-tracker",
  portfolioTrackerStrategy: "/signals/portfolio-tracker/:tradingStrategyId",
  signalDetail: "/signals/:signalId",

  // Account
  billing: "/billing",
  profile: "/profile",
  profileEdit: "/profile/edit",
  profileEmail: "/profile/email",
  profilePassword: "/profile/password",
  profileCommunications: "/profile/communications",
  profileTradingView: "/profile/tradingview",

  // The customer's own discounts on Scryer. Separate from partners below, which is
  // other companies — two entries both called offers is the misreading to avoid.
  offers: "/offers",

  // The tools we refer out to. Deliberately not "affiliate" or "referrals": WI #106
  // is the inbound programme where customers promote Scryer, and two features with
  // those names pointing opposite ways is a support ticket. The path keeps its old
  // name so existing links and emails still land.
  partnerOffers: "/partner-offers",

  // Signal Management (preview)
  signalManagementPreview: "/signal-management",

  // Help centre — two routes, permanently. A new article is a new Markdown
  // file under src/content/help; it never adds a route here.
  help: "/help",
  helpArticle: "/help/:slug",

  // Info / footer
  /** Retired — now a help article. Kept only so the route can redirect. */
  howItWorks: "/how-it-works",
  /** The three published legal documents. Slugs and paths come from
      @scryerlabs/legal and are identical on the brochure site. */
  terms: "/terms",
  privacy: "/privacy",
  disclosures: "/disclosures",
  contact: "/contact",
  /** Third-party notices. Apache 2.0 wants them somewhere a user can actually reach. */
  notices: "/notices",
} as const;

/** Build a canonical article URL. Slugs are flat — category is metadata. */
export const helpArticlePath = (slug: string) => `/help/${slug}`;

/** The request wizard with a tier already picked, from Browse memberships or Send again. */
export const requestMembershipPath = (tierId: string) =>
  `${routePaths.requestMembership}?tier=${encodeURIComponent(tierId)}`;
export const membershipDetailsPath = (tierId: string) => `/memberships/${tierId}`;
export const membershipEndPath = (tierId: string) => `/memberships/end/${tierId}`;
export const membershipResumePath = (tierId: string) => `/memberships/resume/${tierId}`;
export const addOnDetailsPath = (addOnId: string) => `/add-ons/${addOnId}`;
export const addOnEndPath = (addOnId: string) => `/add-ons/end/${addOnId}`;
export const addOnResumePath = (addOnId: string) => `/add-ons/resume/${addOnId}`;
export const signalDetailPath = (signalId: string) => `/signals/${signalId}`;
export const portfolioTrackerStrategyPath = (tradingStrategyId: string) =>
  `/signals/portfolio-tracker/${tradingStrategyId}`;

export const myStrategyDetailPath = (strategyId: string) => `/my-strategies/${strategyId}`;

/** Retired articles. Links to them already went out and cannot be recalled, so they redirect. */
export const retiredHelpArticleSlugs = [
  "invite-only-script",
  "tradingview-plan",
  "tradingview-automation",
  "trading-manually",
  "traderspost-setup",
] as const;
